<template>
  <v-parallax class="hero" src="/img/hero-room.webp" alt="Jasmine Therapies treatment room">
    <div class="hero-banner fill-height d-flex flex-column justify-center">
      <div class="intro-text d-flex flex-column justify-center pt-0 pa-8">
        <div class="text-center text-subtitle-1 pa-2">You are one step away from</div>
        <div class="text-center text-h2 pa-2">balanced harmony</div>
      </div>
      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.Reflexology.Default">Book now!</v-btn>
      </div>
    </div>
  </v-parallax>

  <section id="why-choose">
    <div class="full-width-header">Why choose our reflexology?</div>
    <div class="content pb-16">
      <div class="feature-grid">
        <div class="feature-item" v-for="item in whyChoose" :key="item.title">
          <div class="feature-title">{{ item.title }}</div>
          <div class="feature-text">{{ item.description }}</div>
        </div>
      </div>

      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.Reflexology.Default">Book now!</v-btn>
      </div>
    </div>
  </section>

  <section id="why-choose">
    <div class="full-width-header">Trusted by our clients</div>
    <div class="content pb-16">
      <div class="quote-block" v-for="review in featureReviews" :key="review.name">
        <img class="quote-stars" :src="starsImage" alt="Five stars" />
        <p class="quote-text" v-html="review.quote"></p>
        <div class="quote-author">{{ review.name }}</div>
      </div>

      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.Reflexology.Default">Book now!</v-btn>
      </div>
    </div>
  </section>

  <BenefitsSectionComponent
    header="What are the benefits of Reflexology?"
    :benefits="benefits"
    :booking-url="$bookingUrls.Reflexology.Default" />

  <section id="pricing">
    <div class="full-width-header">Individual Sessions & Packages</div>
    <div class="content pb-16">
      <p class="content-intro">
        Choose between our convenient package options or flexible individual sessions. Book now to experience the
        restorative effects of reflexology and take the first step towards better balance and well-being!
      </p>

      <div class="service-list d-flex flex-wrap justify-center ga-12 px-8">
        <ServiceItemComponent
          title="5-Session Packages"
          :booking-options="packageOptions"
          :link-target="'_self'"
          class="service-item-card" />
        
        <ServiceItemComponent
          title="Individual Sessions"
          :booking-options="sessionOptions"
          :link-target="'_self'"
          class="service-item-card" />
      </div>
    </div>
  </section>

  <section id="cta">
    <div class="full-width-header">Ready to feel restored?</div>
    <div class="content pb-16">
      <p class="content-intro">
        Take the first step towards better balance and well-being. Book your reflexology session today.
      </p>
      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.Reflexology.Default">Book now!</v-btn>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { BookingUrls } from '@/constants/booking';
import BookingOption from '@/models/BookingOption';
import BenefitsSectionComponent from '@/components/BenefitsSectionComponent.vue';
import ServiceItemComponent from '@/components/ServiceItemComponent.vue';

export default defineComponent({
  name: 'ReflexologyView',

  components: {
    BenefitsSectionComponent,
    ServiceItemComponent
  },

  data() {
    return {
      whyChoose: [
        {
          title: 'Certified Expertise',
          description: 'Trained in specialized reflexology techniques with years of proven results for clients.'
        },
        {
          title: 'Holistic Approach',
          description: 'We assess your unique needs and create a tailored treatment plan that evolves with your progress.'
        },
        {
          title: 'Whole-Body Benefits',
          description: 'By targeting pressure points on the feet, reflexology promotes balance throughout the entire body.'
        }
      ],
      starsImage: '/img/five-stars.png',
      packageOptions: [
        new BookingOption('5 x 50 min', BookingUrls.Reflexology.Package50)
      ],
      sessionOptions: [
        new BookingOption('50 min', BookingUrls.Reflexology.Min50)
      ],
      benefits: [
        {
          title: 'Restores Balance',
          description: 'Pressure point techniques on the feet help rebalance the body\'s natural energy pathways and promote overall harmony.'
        },
        {
          title: 'Enhances Circulation',
          description: 'Stimulates blood flow throughout the body, supporting vitality and natural healing processes.'
        },
        {
          title: 'Relieves Deep Tension',
          description: 'Targeted foot work releases tension held across the whole body, offering a uniquely calming experience.'
        }
      ],
      featureReviews: [
        {
          name: "Happy Client",
          quote:
            "I recently had the experience of receiving massage therapy sessions from Jasmine, and I must say, she was excellent. From the moment I stepped into her clinic, I felt welcomed and at ease.<br><br>Jasmine's professionalism and politeness were exceptional. She took the time to listen attentively to my concerns and thoroughly understood the ailments affecting my body. Her extensive knowledge and understanding of various ailments were evident as she explained the causes and potential treatments for my back pain and trapped nerves.<br><br>The relief I experienced after just a few sessions with Jasmine was incredible. She was skilled at targeting the problem areas with precision and care.<br><br>I cannot recommend Jasmine enough. Whether you're seeking relief from specific ailments or simply looking for a relaxing massage experience."
        }
      ]
    };
  }
});
</script>

<style scoped>
.testimonial-list {
  display: flex;
  flex-direction: column;
  gap: 40px;
  max-width: 1100px;
  margin: 0 auto;
}

.testimonial-item {
  border-left: 2px solid rgba(var(--v-theme-primary), 0.8);
  padding: 8px 0 8px 24px;
  color: rgb(var(--v-theme-primary));
}

.testimonial-stars {
  width: 120px;
  margin-bottom: 12px;
}

.testimonial-quote {
  font-size: 22px;
  line-height: 1.6;
  font-style: italic;
  margin: 0;
}

.testimonial-name {
  margin-top: 16px;
  font-weight: 600;
  font-size: 16px;
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  max-width: 900px;
  margin: 0 auto;
}

.contact-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border-radius: 16px;
  background: rgba(var(--v-theme-primary), 0.06);
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}
</style>
